"use client";

import type { PostHog } from "posthog-js";
import { onUserInteraction } from "@/lib/onUserInteraction";

let client: PostHog | null = null;
let initScheduled = false;
let initPromise: Promise<PostHog | null> | null = null;

async function initPostHog(): Promise<PostHog | null> {
  if (typeof window === "undefined") return null;
  if (client?.__loaded) return client;

  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!posthogKey) return null;

  const apiHost =
    process.env.NEXT_PUBLIC_POSTHOG_HOST?.startsWith("/")
      ? process.env.NEXT_PUBLIC_POSTHOG_HOST
      : "/ingest";

  const { default: posthog } = await import("posthog-js");

  if (!posthog.__loaded) {
    posthog.init(posthogKey, {
      api_host: apiHost,
      ui_host: "https://us.posthog.com",
      capture_pageview: false,
      persistence: "localStorage+cookie",
      loaded: (instance) => {
        if (process.env.NODE_ENV === "development") instance.debug();
      },
    });
  }

  client = posthog;
  return posthog;
}

/** Load + init PostHog only after a real user gesture (keeps SDK out of first paint). */
export function schedulePostHogInit() {
  if (typeof window === "undefined" || initScheduled) return;
  initScheduled = true;

  return onUserInteraction(() => {
    initPromise ??= initPostHog().then((ph) => {
      if (ph?.__loaded) {
        ph.capture("$pageview", { $current_url: window.location.href });
      }
      return ph;
    });
  });
}

export async function capturePageview(url: string) {
  // Never force-load the SDK here — only capture after gesture-based init.
  if (initPromise) {
    const ph = await initPromise;
    if (ph?.__loaded) ph.capture("$pageview", { $current_url: url });
    return;
  }
  if (client?.__loaded) {
    client.capture("$pageview", { $current_url: url });
  }
}
